import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import {
  RSVPVisibilityFriends,
  RSVPVisibilityPrivate,
  RSVPVisibilityPublic,
  type RSVPVisibility,
} from '@/lib/api/apiTypes';
import { cn } from '@/lib/utils';
import { LockIcon, UsersIcon, UsersRoundIcon, type LucideIcon } from 'lucide-react-native';
import { Pressable, View } from 'react-native';

type RSVPVisibilityOption = {
  value: RSVPVisibility;
  label: string;
  description: string;
  icon: LucideIcon;
};

const RSVP_VISIBILITY_OPTIONS: RSVPVisibilityOption[] = [
  {
    value: RSVPVisibilityPublic,
    label: 'Everyone',
    description: 'Visible to anyone',
    icon: UsersRoundIcon,
  },
  {
    value: RSVPVisibilityFriends,
    label: 'Friends only',
    description: 'Mutual follows only',
    icon: UsersIcon,
  },
  {
    value: RSVPVisibilityPrivate,
    label: 'Only me',
    description: 'Private',
    icon: LockIcon,
  },
];

export function RsvpVisibilitySelector({
  visibility,
  disabled,
  onChangeVisibility,
}: {
  visibility: RSVPVisibility;
  disabled?: boolean;
  onChangeVisibility: (value: RSVPVisibility) => void;
}) {
  return (
    <View className="overflow-hidden rounded-[16px] border border-border bg-input/20">
      {RSVP_VISIBILITY_OPTIONS.map((option) => (
        <VisibilityOptionRow
          key={option.value}
          option={option}
          active={visibility === option.value}
          disabled={Boolean(disabled)}
          onPress={() => onChangeVisibility(option.value)}
        />
      ))}
    </View>
  );
}

export function rsvpVisibilityExplainer(visibility: RSVPVisibility) {
  switch (visibility) {
    case RSVPVisibilityPublic:
      return 'Mutual friends nearby may be notified';
    case RSVPVisibilityFriends:
      return "Your friends will see you're going. No notification sent";
    case RSVPVisibilityPrivate:
    default:
      return 'No one will be notified';
  }
}

function VisibilityOptionRow({
  option,
  active,
  disabled,
  onPress,
}: {
  option: RSVPVisibilityOption;
  active: boolean;
  disabled: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      className={cn(
        'flex-row items-center gap-3 border-b border-border px-3 py-3 active:opacity-80',
        active ? 'bg-primary/10' : 'opacity-70',
        disabled && 'opacity-50'
      )}
      disabled={disabled}
      onPress={onPress}>
      <View
        className={cn(
          'size-8 items-center justify-center rounded-full',
          active ? 'bg-primary/15' : 'bg-surface-raised'
        )}>
        <Icon as={option.icon} size={15} className={active ? 'text-primary' : 'text-muted-foreground'} />
      </View>
      <View className="flex-1 gap-0.5">
        <Text className={cn('text-[14px] font-semibold', active ? 'text-foreground' : 'text-muted-foreground')}>
          {option.label}
        </Text>
        <Text className="text-[12px] leading-4 text-muted-foreground">{option.description}</Text>
      </View>
      <View
        className={cn(
          'size-5 items-center justify-center rounded-full border',
          active ? 'border-primary' : 'border-border-strong'
        )}>
        {active ? <View className="size-3 rounded-full bg-primary" /> : null}
      </View>
    </Pressable>
  );
}
